import { useEffect, useRef } from 'react'
import { ApolloError, useSubscription } from '@apollo/client'
import {
  REFRESH_APPROVAL_FLOW_SUBSCRIPTION,
  RefreshApprovalFlowPayload,
  RefreshApprovalFlowSubscriptionResponse,
  RefreshApprovalFlowSubscriptionVariables,
} from 'queries/subscriptions/refresh-approval-flow'

export interface UseRefreshApprovalFlowSubscriptionOptions {
  onData?: (payload: RefreshApprovalFlowPayload) => void
  onError?: (error: ApolloError) => void
}

interface UseRefreshApprovalFlowSubscriptionResult {
  data: RefreshApprovalFlowPayload | undefined
  loading: boolean
  error: ApolloError | undefined
}

/**
 * Starts the `onRefreshApprovalFlow` subscription for the given dealId.
 * Runs in the background as long as the component is mounted.
 *
 * Callbacks are stored in refs so Apollo's subscription handler always invokes
 * the latest version, even if the caller's closure updates after the subscription
 * is first created.
 *
 * @param dealId - The deal to subscribe to. Subscription is skipped when undefined.
 * @param options.onData  - Called each time a new subscription payload arrives.
 * @param options.onError - Called when the subscription encounters an error.
 */
export const useRefreshApprovalFlowSubscription = (
  dealId: number | undefined,
  options: UseRefreshApprovalFlowSubscriptionOptions = {}
): UseRefreshApprovalFlowSubscriptionResult => {
  // Keep refs to the latest callbacks so the Apollo handler is never stale.
  const onDataRef = useRef(options.onData)
  const onErrorRef = useRef(options.onError)

  useEffect(() => {
    onDataRef.current = options.onData
    onErrorRef.current = options.onError
  }, [options.onData, options.onError])

  const { data, loading, error } = useSubscription<
    RefreshApprovalFlowSubscriptionResponse,
    RefreshApprovalFlowSubscriptionVariables
  >(REFRESH_APPROVAL_FLOW_SUBSCRIPTION, {
    variables: { dealId: dealId ?? 0 },
    skip: !dealId,
    fetchPolicy: 'no-cache',
    onData: ({ data: { data: payload } }) => {
      if (payload?.onRefreshApprovalFlow) {
        onDataRef.current?.(payload.onRefreshApprovalFlow)
      }
    },
    onError: (err) => {
      console.error('Refresh approval flow subscription error:', err)
      onErrorRef.current?.(err)
    },
  })

  return {
    data: data?.onRefreshApprovalFlow,
    loading,
    error,
  }
}
